import styled from 'styled-components';
import media from 'src/styles/Media.styled';
import {
    FONT_FAMILY_REGULAR,
    FONT_FAMILY_DEMI,
    DARK_NAVY_FONT_COLOR,
    PRIMARY_FONT_COLOR,
    SECONDARY_FONT_COLOR,
    SMALL_TEXT_PX
} from 'src/styles/fonts/MCFontConstants';

/**
 * Consigned Inventory - styled components (SCAFFOLD)
 * Layout mirrors the Field Inventory Dashboard structure at a high level.
 */

export const ConsignedContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 8px;
    min-height: 0;
    overflow: hidden;
    padding: ${({isMobile}) => isMobile ? '8px 0px 24px 0px' : '8px 10px 24px 0px'};
    width: 100%;
    box-sizing: border-box;

    @media only screen and (min-width: 768px) and (max-width: 1366px) and (orientation: landscape) and (hover: none) and (pointer: coarse) {
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
    }
`;

/* ---- Combined Search and Results Section ---- */
export const SearchAndResultsCard = styled.div`
    display: flex;
    flex-direction: column;
    background: transparent;
    border-radius: 0;
    padding: 0;
    gap: 12px;
    box-shadow: 0px -3px 6px #00000029 !important;
    
    ${media.mobileDevice`
        background: #ffffff;
        border-radius: 0;
        box-shadow: none;
        padding: 12px 16px;
        
        /* Remove nested card styling from SearchAndFilterContainer in mobile view only */
        > div:first-child {
            background: transparent !important;
            box-shadow: none !important;
            border-radius: 0 !important;
            padding: 0 !important;
            margin: 0 !important;
            width: 100% !important;
        }
    `}
`;

/* ---- Search row ---- */
export const SearchCard = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    background: #ffffff;
    border-radius: 8px;
    padding: 0 20px;
    ${media.mobileDevice`
        flex-direction: column;
        align-items: stretch;
        gap: 8px;
    `}
`;

export const SearchLabel = styled.span`
    font-family: ${FONT_FAMILY_DEMI};
    font-size: 16px;
    color: ${DARK_NAVY_FONT_COLOR};
    white-space: nowrap;
`;

export const SearchDropdownWrap = styled.div`
    min-width: 200px;
    ${media.mobileDevice`
        width: 100%;
    `}
`;

export const SearchBoxWrap = styled.div`
    flex: 1;
    ${media.mobileDevice`
        width: 100%;
    `}
`;

/* ---- Results header (mirrors FIM FilterRow) ---- */
export const ResultsHeaderRow = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
    gap: 8px;
    width: 100%;
    margin: 0px;
    padding: 0px 0px 0px 10px;
    ${media.mobileDevice`
        flex-direction: column;
        align-items: flex-start;
        padding: 0;
    `}
`;

export const ResultsLeft = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 0;
`;

export const ResultsCount = styled.div`
    font-family: 'AvenirNextWorld Demi';
    font-size: ${({ fontSize }) => (fontSize ? fontSize : '18px')};
    color: #140f4b;
    white-space: nowrap;
    flex-shrink: 0;
`;

export const DesktopFilterPanelWrapper = styled.div`
    display: flex;
    align-items: center;
    min-width: 0;

    .MuiGrid-root.MuiGrid-container {
        align-items: center;
        flex-wrap: nowrap;
        justify-content: flex-start;
    }

    .MuiGrid-root.MuiGrid-item {
        max-width: none;
        width: auto;
    }

    .MuiGrid-root.MuiGrid-item:first-child {
        flex: 0 1 auto;
        min-width: 0;
    }

    .MuiGrid-root.MuiGrid-item:last-child {
        background-color: transparent !important;
        flex: 0 0 auto;
        margin-left: 8px;
    }

    ${media.mobileDevice`
        display: none;
    `}
`;

export const MobileFilterPanelWrapper = styled.div`
    display: none;

    ${media.mobileDevice`
        display: block;
        width: 100%;
        margin-top: -5px;

        .MuiGrid-root.MuiGrid-container {
            align-items: center;
            flex-wrap: nowrap;
            justify-content: space-between;
            width: 100%;
        }

        .MuiGrid-root.MuiGrid-item {
            max-width: none;
        }

        .MuiGrid-root.MuiGrid-item:first-child {
            flex: 1 1 auto;
            min-width: 0;
        }

        .MuiGrid-root.MuiGrid-item:last-child {
            background-color: transparent !important;
            flex: 0 0 auto;
            margin-left: 8px;
            text-align: right;
        }
    `}
`;

export const ResultsRight = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-shrink: 0;
    justify-content: flex-end;
    gap: 8px;
    ${media.mobileDevice`
        width: 100%;
        max-width: 98%;
        justify-content: flex-end;
        padding-top: 4px;
    `}
`;

export const ExportIconImg = styled.img`
    margin-right: 5px;
    width: 12px;
    height: 12px;
`;

export const ExportSpinnerWrapper = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 8px;
`;

/* ---- Table (mirrors FIM TableWrapper card look) ---- */
export const TableCard = styled.div`
    background: #ffffff;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.1), 0px 0px 6px #00000029 !important;
    ${media.mobileDevice`
        border-radius: 0;
        box-shadow: none !important;
    `}
`;

export const TableWrapper = styled.div`
    width: 100%;
    overflow-x: auto;
`;

export const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
`;

export const Th = styled.th`
    text-align: ${({ $key }) => ($key === 'onHand' ? 'center' : 'left')};
    font-family: ${FONT_FAMILY_DEMI};
    font-size: 13px;
    color: ${DARK_NAVY_FONT_COLOR};
    padding: 14px 16px;
    border-bottom: 1px solid #e6e6e6;
    white-space: nowrap;
`;

export const SortIcon = styled.span`
    margin-left: 4px;
    color: ${SECONDARY_FONT_COLOR};
    font-size: 11px;
`;

export const Td = styled.td`
    font-family: ${FONT_FAMILY_REGULAR};
    font-size: 13px;
    color: ${PRIMARY_FONT_COLOR};
    padding: 14px 16px;
    border-bottom: 1px solid #f0f0f0;
    white-space: nowrap;
    text-align: ${({ $key }) => ($key === 'onHand' ? 'center' : 'left')};
`;

export const Tr = styled.tr`
    &:nth-child(even) {
        background: #fafafa;
    }
`;

/* ---- Pagination (uses common BasicPagination) ---- */
export const PaginationWrapper = styled.div`
    width: 100%;
    background: #ffffff;
    border-radius: 8px;
    position: absolute;
    bottom: 100px;
    max-width: 78.5%;

    ${media.mobileDevice`
        border-radius: 0;        
        max-width: 100%;
    `}
`;

export const EmptyResultsMessage = styled.div`
    text-align: start;
    padding: ${({isMobile}) => (isMobile ? '0px 20px' : '0px 10px 0px 10px')};
    font-size: ${SMALL_TEXT_PX};
    color: ${SECONDARY_FONT_COLOR};
    font-family: ${FONT_FAMILY_REGULAR};
`;
